import { useState } from "react";
import { Box, Input, InputGroup, InputLeftAddon, Stack, Text } from "@chakra-ui/react";
import type { Pricing } from "../api/events";
import { centsToUsd, usdToCents } from "../lib/money";

const EMPTY: Pricing = { creator_verification: null, work_certification: null, free_works: null };

export function PricingFields({
  value,
  onChange,
  editing,
}: {
  value: Pricing | null;
  onChange: (p: Pricing) => void;
  editing: boolean;
}) {
  const v = value ?? EMPTY;
  // Dollar fields hold raw string state while typing and commit to the parent
  // (cents) on blur. Deriving the input value directly from cents via
  // centsToUsd (which forces .toFixed(2)) would reformat mid-keystroke, strand
  // the caret, and block multi-digit / decimal entry. Resync from props after a
  // commit (or external change) normalizes the display.
  const [cv, setCv] = useState(() => centsToUsd(v.creator_verification));
  const [wc, setWc] = useState(() => centsToUsd(v.work_certification));
  const [prevCvCents, setPrevCvCents] = useState(v.creator_verification);
  if (v.creator_verification !== prevCvCents) {
    setPrevCvCents(v.creator_verification);
    setCv(centsToUsd(v.creator_verification));
  }
  const [prevWcCents, setPrevWcCents] = useState(v.work_certification);
  if (v.work_certification !== prevWcCents) {
    setPrevWcCents(v.work_certification);
    setWc(centsToUsd(v.work_certification));
  }
  const commitDollars = (k: "creator_verification" | "work_certification", raw: string) => {
    onChange({ ...v, [k]: usdToCents(raw) });
  };
  const setCount = (k: "free_works", raw: string) => {
    const n = raw.trim() === "" ? null : Math.max(0, Math.trunc(Number(raw)));
    onChange({ ...v, [k]: Number.isNaN(n as number) ? null : n });
  };
  const certFree = v.work_certification === 0;
  const num = (n: number | null) => (n === null || n === undefined ? "" : String(n));
  return (
    <Stack spacing={3}>
      <Box>
        <Text fontSize="xs" color="gray.500" mb={1}>Creator verification ($, 0 = free)</Text>
        {editing ? (
          <InputGroup size="sm">
            <InputLeftAddon>$</InputLeftAddon>
            <Input type="number" step="0.01" min={0} value={cv}
              onChange={(e) => setCv(e.target.value)}
              onBlur={() => commitDollars("creator_verification", cv)} />
          </InputGroup>
        ) : (
          <Text>{v.creator_verification === null ? "—" : `$${centsToUsd(v.creator_verification)}`}</Text>
        )}
      </Box>
      <Box>
        <Text fontSize="xs" color="gray.500" mb={1}>Work certification ($, 0 = free)</Text>
        {editing ? (
          <InputGroup size="sm">
            <InputLeftAddon>$</InputLeftAddon>
            <Input type="number" step="0.01" min={0} value={wc}
              onChange={(e) => setWc(e.target.value)}
              onBlur={() => commitDollars("work_certification", wc)} />
          </InputGroup>
        ) : (
          <Text>{v.work_certification === null ? "—" : `$${centsToUsd(v.work_certification)}`}</Text>
        )}
      </Box>
      <Box>
        <Text fontSize="xs" color="gray.500" mb={1}>Free works (count)</Text>
        {certFree ? (
          <Text color="gray.500">Unlimited (certification is free)</Text>
        ) : editing ? (
          <Input size="sm" type="number" min={0} value={num(v.free_works)}
            onChange={(e) => setCount("free_works", e.target.value)} />
        ) : (
          <Text>{v.free_works === null ? "—" : String(v.free_works)}</Text>
        )}
      </Box>
    </Stack>
  );
}
